import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Grid, Paper, Typography, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Button, TextField, Stack, LinearProgress
} from '@mui/material';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import PageHeader from '../../components/common/PageHeader';
import ModuleControlPanel from '../../components/common/ModuleControlPanel';
import StatCard from '../../components/common/StatCard';
import { loadAnalysisJobs, submitAnalysisSample } from './malwareAnalysisSlice';
import { formatRelativeTime } from '../../utils/severity';
import { tokens } from '../../theme/theme';

const VERDICT_COLOR = {
  malicious: tokens.severity.critical,
  suspicious: tokens.severity.medium,
  clean: tokens.severity.low
};

const STATUS_COLOR = {
  completed: tokens.severity.low,
  running: tokens.severity.info,
  queued: tokens.text.muted,
  failed: tokens.severity.critical
};

function scoreColor(score) {
  if (score === null || score === undefined) return tokens.text.muted;
  if (score >= 70) return tokens.severity.critical;
  if (score >= 35) return tokens.severity.medium;
  return tokens.severity.low;
}

export default function MalwareAnalysisPage() {
  const dispatch = useDispatch();
  const { jobs } = useSelector((s) => s.malwareAnalysis);
  const [filename, setFilename] = useState('');
  const [sha256, setSha256] = useState('');

  useEffect(() => { dispatch(loadAnalysisJobs()); }, [dispatch]);

  const malicious = jobs.filter((j) => j.verdict === 'malicious').length;
  const suspicious = jobs.filter((j) => j.verdict === 'suspicious').length;
  const clean = jobs.filter((j) => j.verdict === 'clean').length;

  const normalizedSha256 = sha256.trim();
  const sha256Valid = /^[A-Fa-f0-9]{64}$/.test(normalizedSha256);

  const handleSubmit = () => {
    if (!filename.trim() || !sha256Valid) return;
    dispatch(submitAnalysisSample({ filename: filename.trim(), sha256: normalizedSha256 }));
    setFilename('');
    setSha256('');
  };

  return (
    <Box>
      <PageHeader
        title="Malware Analysis"
        subtitle="Static analysis, YARA-style rule matching, and hash reputation for samples forwarded from Email Security, Endpoint Analysis, and Threat Detection"
        action={<ModuleControlPanel basePath="/malware-analysis" moduleLabel="Malware Analysis" method="patch" />}
      />

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={3}><StatCard label="Samples Analyzed" value={jobs.length} icon={BugReportRoundedIcon} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Malicious" value={malicious} accent={tokens.severity.critical} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Suspicious" value={suspicious} accent={tokens.severity.medium} /></Grid>
        <Grid item xs={12} sm={3}><StatCard label="Clean" value={clean} accent={tokens.severity.low} /></Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2.4 }}>
            <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Submit Sample</Typography>
            <Stack spacing={1.5} sx={{ mt: 1.5 }}>
              <TextField
                size="small" placeholder="Filename, e.g. invoice_4471.pdf.exe"
                value={filename} onChange={(e) => setFilename(e.target.value)}
              />
              <TextField
                size="small" label="SHA-256" placeholder="64-character SHA-256 digest"
                value={sha256} onChange={(e) => setSha256(e.target.value)}
                required error={Boolean(normalizedSha256) && !sha256Valid}
                helperText={Boolean(normalizedSha256) && !sha256Valid ? "Enter exactly 64 hexadecimal characters." : "Required when submitting by hash. File-content submissions can derive SHA-256 from the actual bytes."}
              />
              <Button variant="contained" startIcon={<PlayArrowRoundedIcon />} onClick={handleSubmit} disabled={!filename.trim() || !sha256Valid}>
                Submit for Analysis
              </Button>
              <Typography variant="caption" sx={{ color: tokens.text.muted }}>
                Static analysis + YARA-style matching + hash reputation. No sample execution — see the module README for the dynamic-analysis roadmap.
              </Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, pb: 0 }}>
              <Typography variant="overline" sx={{ color: tokens.text.muted, fontSize: '0.65rem' }}>Analysis Jobs</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Filename', 'SHA-256', 'Submitted By', 'Status', 'Score', 'Verdict', 'Rules Matched', 'When'].map((h) => (
                    <TableCell key={h} sx={{ color: tokens.text.muted, fontSize: '0.66rem', textTransform: 'uppercase' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.slice(0, 14).map((j) => (
                  <TableRow key={j.id} hover>
                    <TableCell sx={{ fontSize: '0.78rem', color: tokens.text.primary }}>{j.filename}</TableCell>
                    <TableCell sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.68rem', color: tokens.text.muted }}>
                      {j.sha256.slice(0, 12)}…
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.74rem', color: tokens.text.secondary }}>{j.submitted_by_module}</TableCell>
                    <TableCell>
                      <Chip label={j.status} size="small" sx={{ color: STATUS_COLOR[j.status], border: `1px solid ${STATUS_COLOR[j.status]}`, backgroundColor: 'transparent' }} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 90 }}>
                      {j.score !== null ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <LinearProgress
                            variant="determinate" value={j.score}
                            sx={{ width: 48, height: 5, borderRadius: 3, backgroundColor: tokens.bg.elevated, '& .MuiLinearProgress-bar': { backgroundColor: scoreColor(j.score) } }}
                          />
                          <Typography sx={{ fontSize: '0.72rem', color: scoreColor(j.score), fontFamily: '"JetBrains Mono", monospace' }}>{j.score}</Typography>
                        </Box>
                      ) : <Typography sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>—</Typography>}
                    </TableCell>
                    <TableCell>
                      {j.verdict ? (
                        <Chip label={j.verdict} size="small" sx={{ color: VERDICT_COLOR[j.verdict], border: `1px solid ${VERDICT_COLOR[j.verdict]}`, backgroundColor: `${VERDICT_COLOR[j.verdict]}1A` }} />
                      ) : <Typography sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>pending</Typography>}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        {j.yaraMatches.length === 0 && <Typography sx={{ fontSize: '0.72rem', color: tokens.text.muted }}>—</Typography>}
                        {j.yaraMatches.map((r) => (
                          <Chip key={r} label={r} size="small" sx={{ fontSize: '0.62rem', color: tokens.text.secondary, backgroundColor: tokens.bg.elevated, border: `1px solid ${tokens.border}` }} />
                        ))}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem', color: tokens.text.muted, whiteSpace: 'nowrap' }}>{formatRelativeTime(j.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
